import { Problem } from '../../types/problem';

export const controlFlowProblems: Problem[] = [
  {
    id: 'cf-1',
    title: 'Nested Loop Pattern',
    description: `Write a function that prints the following pattern using nested loops:
    *
    **
    ***
    ****
    *****`,
    difficulty: 'Easy',
    tags: ['loops', 'patterns', 'nested-loops'],
    starterCode: `function printPattern(n) {
  // Write your code here
}`,
    testCases: [
      {
        input: [5],
        output: '*\n**\n***\n****\n*****'
      }
    ],
    solution: `function printPattern(n) {
  for (let i = 1; i <= n; i++) {
    let row = '';
    for (let j = 1; j <= i; j++) {
      row += '*';
    }
    console.log(row);
  }
}`,
    hints: [
      'Use an outer loop for rows',
      'Use an inner loop for columns in each row',
      'The number of stars in each row equals the row number'
    ]
  },
  {
    id: 'cf-2',
    title: 'Conditional Array Processing',
    description: `Given an array of numbers, write a function that:
    1. Returns the sum of all even numbers
    2. Returns the product of all odd numbers
    3. Returns an object with both results`,
    difficulty: 'Easy',
    tags: ['conditionals', 'arrays', 'loops'],
    starterCode: `function processArray(arr) {
  // Write your code here
}`,
    testCases: [
      {
        input: [[1, 2, 3, 4, 5]],
        output: { evenSum: 6, oddProduct: 15 }
      }
    ],
    solution: `function processArray(arr) {
  let evenSum = 0;
  let oddProduct = 1;
  
  for (let num of arr) {
    if (num % 2 === 0) {
      evenSum += num;
    } else {
      oddProduct *= num;
    }
  }
  
  return { evenSum, oddProduct };
}`,
    hints: [
      'Use a for...of loop to iterate through the array',
      'Use the modulo operator (%) to check if a number is even or odd',
      'Create an object to store both results'
    ]
  },
  {
    id: 'cf-3',
    title: 'Recursive Fibonacci with Memoization',
    description: `Implement a recursive Fibonacci function with memoization to avoid redundant calculations. 
    The function should cache previously calculated values to improve performance.`,
    difficulty: 'Medium',
    tags: ['recursion', 'memoization', 'dynamic-programming'],
    starterCode: `function fibonacci(n) {
  // Write your code here
}`,
    testCases: [
      {
        input: [10],
        output: 55
      }
    ],
    solution: `function fibonacci(n, memo = {}) {
  if (n in memo) return memo[n];
  if (n <= 1) return n;
  
  memo[n] = fibonacci(n - 1, memo) + fibonacci(n - 2, memo);
  return memo[n];
}`,
    hints: [
      'Use an object to store calculated values',
      'Check if the value exists in memo before calculating',
      'Store the result in memo before returning'
    ]
  },
  {
    id: 'cf-4',
    title: 'Binary Search Tree Traversal',
    description: `Implement three traversal methods for a binary search tree:
    1. In-order traversal
    2. Pre-order traversal
    3. Post-order traversal
    Return the results as arrays.`,
    difficulty: 'Medium',
    tags: ['recursion', 'trees', 'traversal'],
    starterCode: `class TreeNode {
  constructor(val) {
    this.val = val;
    this.left = null;
    this.right = null;
  }
}

function inorderTraversal(root) {
  // Write your code here
}

function preorderTraversal(root) {
  // Write your code here
}

function postorderTraversal(root) {
  // Write your code here
}`,
    testCases: [
      {
        input: [
          {
            val: 4,
            left: {
              val: 2,
              left: { val: 1 },
              right: { val: 3 }
            },
            right: {
              val: 6,
              left: { val: 5 },
              right: { val: 7 }
            }
          }
        ],
        output: {
          inorder: [1, 2, 3, 4, 5, 6, 7],
          preorder: [4, 2, 1, 3, 6, 5, 7],
          postorder: [1, 3, 2, 5, 7, 6, 4]
        }
      }
    ],
    solution: `class TreeNode {
  constructor(val) {
    this.val = val;
    this.left = null;
    this.right = null;
  }
}

function inorderTraversal(root) {
  const result = [];
  function traverse(node) {
    if (!node) return;
    traverse(node.left);
    result.push(node.val);
    traverse(node.right);
  }
  traverse(root);
  return result;
}

function preorderTraversal(root) {
  const result = [];
  function traverse(node) {
    if (!node) return;
    result.push(node.val);
    traverse(node.left);
    traverse(node.right);
  }
  traverse(root);
  return result;
}

function postorderTraversal(root) {
  const result = [];
  function traverse(node) {
    if (!node) return;
    traverse(node.left);
    traverse(node.right);
    result.push(node.val);
  }
  traverse(root);
  return result;
}`,
    hints: [
      'Use recursion for each traversal method',
      'Consider the order of operations in each traversal',
      'Use a helper function to maintain the result array'
    ]
  },
  {
    id: 'cf-5',
    title: 'Stack-based Expression Evaluator',
    description: `Implement a function that evaluates a mathematical expression given as a string.
    The expression can contain:
    - Numbers
    - Basic operators (+, -, *, /)
    - Parentheses
    Handle operator precedence correctly.`,
    difficulty: 'Hard',
    tags: ['stacks', 'parsing', 'evaluation'],
    starterCode: `function evaluateExpression(expression) {
  // Write your code here
}`,
    testCases: [
      {
        input: ['(2 + 3) * 4'],
        output: 20
      }
    ],
    solution: `function evaluateExpression(expression) {
  const tokens = expression.split(' ').filter(token => token);
  const values = [];
  const operators = [];
  
  for (let token of tokens) {
    if (!isNaN(token)) {
      values.push(Number(token));
    } else if (token === '(') {
      operators.push(token);
    } else if (token === ')') {
      while (operators.length && operators[operators.length - 1] !== '(') {
        values.push(applyOperator(operators.pop(), values.pop(), values.pop()));
      }
      operators.pop(); // Remove '('
    } else if (isOperator(token)) {
      while (operators.length && 
             precedence(operators[operators.length - 1]) >= precedence(token)) {
        values.push(applyOperator(operators.pop(), values.pop(), values.pop()));
      }
      operators.push(token);
    }
  }
  
  while (operators.length) {
    values.push(applyOperator(operators.pop(), values.pop(), values.pop()));
  }
  
  return values[0];
}

function isOperator(token) {
  return ['+', '-', '*', '/'].includes(token);
}

function precedence(operator) {
  switch (operator) {
    case '+':
    case '-':
      return 1;
    case '*':
    case '/':
      return 2;
    default:
      return 0;
  }
}

function applyOperator(operator, b, a) {
  switch (operator) {
    case '+': return a + b;
    case '-': return a - b;
    case '*': return a * b;
    case '/': return a / b;
    default: return 0;
  }
}`,
    hints: [
      'Use two stacks: one for values and one for operators',
      'Handle operator precedence using a precedence function',
      'Process parentheses using a stack-based approach'
    ]
  },
  {
    id: 'cf-6',
    title: 'Queue-based Level Order Traversal',
    description: `Implement a function that performs level-order traversal of a binary tree using a queue.
    Return the nodes level by level as a 2D array.`,
    difficulty: 'Medium',
    tags: ['queues', 'trees', 'traversal'],
    starterCode: `function levelOrderTraversal(root) {
  // Write your code here
}`,
    testCases: [
      {
        input: [
          {
            val: 3,
            left: { val: 9 },
            right: {
              val: 20,
              left: { val: 15 },
              right: { val: 7 }
            }
          }
        ],
        output: [[3], [9, 20], [15, 7]]
      }
    ],
    solution: `function levelOrderTraversal(root) {
  if (!root) return [];
  
  const result = [];
  const queue = [root];
  
  while (queue.length) {
    const levelSize = queue.length;
    const currentLevel = [];
    
    for (let i = 0; i < levelSize; i++) {
      const node = queue.shift();
      currentLevel.push(node.val);
      
      if (node.left) queue.push(node.left);
      if (node.right) queue.push(node.right);
    }
    
    result.push(currentLevel);
  }
  
  return result;
}`,
    hints: [
      'Use a queue to keep track of nodes at each level',
      'Process all nodes at the current level before moving to the next',
      'Keep track of the level size to separate levels in the result'
    ]
  },
  {
    id: 'cf-7',
    title: 'Conditional String Processing',
    description: `Write a function that processes a string according to these rules:
    1. If the string contains only letters, convert it to uppercase
    2. If the string contains only numbers, convert it to an integer and multiply by 2
    3. If the string contains both, return the original string
    4. If the string is empty, return an empty string`,
    difficulty: 'Easy',
    tags: ['conditionals', 'strings', 'type-checking'],
    starterCode: `function processString(str) {
  // Write your code here
}`,
    testCases: [
      {
        input: ['hello'],
        output: 'HELLO'
      },
      {
        input: ['123'],
        output: 246
      },
      {
        input: ['hello123'],
        output: 'hello123'
      },
      {
        input: [''],
        output: ''
      }
    ],
    solution: `function processString(str) {
  if (!str) return '';
  
  const hasLetters = /[a-zA-Z]/.test(str);
  const hasNumbers = /[0-9]/.test(str);
  
  if (hasLetters && !hasNumbers) {
    return str.toUpperCase();
  } else if (!hasLetters && hasNumbers) {
    return parseInt(str) * 2;
  } else {
    return str;
  }
}`,
    hints: [
      'Use regular expressions to check for letters and numbers',
      'Handle empty string case first',
      'Use logical operators to combine conditions'
    ]
  },
  {
    id: 'cf-8',
    title: 'Recursive Array Flattening',
    description: `Implement a function that flattens a nested array of arbitrary depth.
    The function should handle arrays containing numbers, strings, and other arrays.`,
    difficulty: 'Medium',
    tags: ['recursion', 'arrays', 'flattening'],
    starterCode: `function flattenArray(arr) {
  // Write your code here
}`,
    testCases: [
      {
        input: [[1, [2, [3, 4]], 5]],
        output: [1, 2, 3, 4, 5]
      }
    ],
    solution: `function flattenArray(arr) {
  return arr.reduce((flat, item) => {
    if (Array.isArray(item)) {
      return flat.concat(flattenArray(item));
    }
    return flat.concat(item);
  }, []);
}`,
    hints: [
      'Use recursion to handle nested arrays of any depth',
      'Use Array.isArray() to check if an item is an array',
      'Use reduce to accumulate flattened results'
    ]
  },
  {
    id: 'cf-9',
    title: 'Loop-based Matrix Rotation',
    description: `Implement a function that rotates a square matrix 90 degrees clockwise.
    The rotation should be done in-place without using extra space.`,
    difficulty: 'Hard',
    tags: ['loops', 'matrices', 'in-place'],
    starterCode: `function rotateMatrix(matrix) {
  // Write your code here
}`,
    testCases: [
      {
        input: [
          [
            [1, 2, 3],
            [4, 5, 6],
            [7, 8, 9]
          ]
        ],
        output: [
          [7, 4, 1],
          [8, 5, 2],
          [9, 6, 3]
        ]
      }
    ],
    solution: `function rotateMatrix(matrix) {
  const n = matrix.length;
  
  // Transpose the matrix
  for (let i = 0; i < n; i++) {
    for (let j = i; j < n; j++) {
      [matrix[i][j], matrix[j][i]] = [matrix[j][i], matrix[i][j]];
    }
  }
  
  // Reverse each row
  for (let i = 0; i < n; i++) {
    matrix[i].reverse();
  }
  
  return matrix;
}`,
    hints: [
      'First transpose the matrix (swap rows and columns)',
      'Then reverse each row to get the final rotation',
      'Use array destructuring for swapping elements'
    ]
  },
  {
    id: 'cf-10',
    title: 'Conditional Object Transformation',
    description: `Write a function that transforms an object based on these rules:
    1. If a value is a number, multiply it by 2
    2. If a value is a string, convert it to uppercase
    3. If a value is an array, filter out all even numbers
    4. If a value is an object, recursively transform it
    5. Leave other types unchanged`,
    difficulty: 'Medium',
    tags: ['conditionals', 'objects', 'recursion'],
    starterCode: `function transformObject(obj) {
  // Write your code here
}`,
    testCases: [
      {
        input: [
          {
            a: 1,
            b: 'hello',
            c: [1, 2, 3, 4],
            d: {
              e: 5,
              f: 'world'
            }
          }
        ],
        output: {
          a: 2,
          b: 'HELLO',
          c: [1, 3],
          d: {
            e: 10,
            f: 'WORLD'
          }
        }
      }
    ],
    solution: `function transformObject(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.filter(item => typeof item !== 'number' || item % 2 !== 0);
  }
  
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'number') {
      result[key] = value * 2;
    } else if (typeof value === 'string') {
      result[key] = value.toUpperCase();
    } else if (Array.isArray(value)) {
      result[key] = value.filter(item => typeof item !== 'number' || item % 2 !== 0);
    } else if (typeof value === 'object' && value !== null) {
      result[key] = transformObject(value);
    } else {
      result[key] = value;
    }
  }
  
  return result;
}`,
    hints: [
      'Use typeof to check value types',
      'Handle arrays and objects separately',
      'Use recursion for nested objects'
    ]
  }
];
